import React, { useEffect, useMemo, useState } from 'react';
import Dialog from './TivigoDialog';
import CircularProgress from '@mui/material/CircularProgress';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RedeemRoundedIcon from '@mui/icons-material/RedeemRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { formatCreditAmount, getCreditCurrency } from './creditCurrency';
import './userPanelTransactionHistoryModal.css';

const FILTERS = [
  { id: 'all', label: 'Tümü', Icon: HistoryRoundedIcon },
  { id: 'income', label: 'Yüklemeler', Icon: TrendingUpRoundedIcon },
  { id: 'expense', label: 'Harcamalar', Icon: TrendingDownRoundedIcon },
];

const PAGE_SIZE = 5;
const now = Math.floor(Date.now() / 1000);

const PREVIEW_HISTORY = [
  { id: 109, onceki: 1750, sonraki: 2750, harcanan: 0, kazanc: 1000, aciklama: 'Kredi yükleme işlemi', zaman: now - 840 },
  { id: 108, onceki: 2750, sonraki: 2050, harcanan: 700, kazanc: 0, aciklama: '3 aylık abonelik süresi eklendi', zaman: now - 14800 },
  { id: 107, onceki: 1500, sonraki: 1750, harcanan: 0, kazanc: 250, aciklama: 'Referans bonusu hesabınıza tanımlandı', zaman: now - 86400 },
  { id: 106, onceki: 1500, sonraki: 1500, harcanan: 0, kazanc: 1000, aciklama: 'Kredi yükleme işlemi banka tarafından reddedildi', zaman: now - 172800 },
  { id: 105, onceki: 2050, sonraki: 1550, harcanan: 500, kazanc: 0, aciklama: 'Alt kullanıcı ekleme işlemi', zaman: now - 259200 },
  { id: 104, onceki: 1550, sonraki: 950, harcanan: 600, kazanc: 0, aciklama: 'Bayi paket satın alma işlemi', zaman: now - 345600 },
  { id: 103, onceki: 450, sonraki: 1450, harcanan: 0, kazanc: 1000, aciklama: 'Kredi yükleme işlemi', zaman: now - 432000 },
  { id: 102, onceki: 450, sonraki: 450, harcanan: 0, kazanc: 0, aciklama: 'Bakiye kontrolü tamamlandı', zaman: now - 518400 },
  { id: 101, onceki: 450, sonraki: 450, harcanan: 700, kazanc: 0, aciklama: 'Abonelik uzatma işlemi tamamlanamadı', zaman: now - 604800 },
];

const numberValue = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const originalEurQuote = (item) => {
  const structuredCurrency = String(item?.currency || item?.native_currency || '').toUpperCase();
  const structuredAmount = Number(item?.native_amount);
  const structuredRate = Number(item?.fx_rate ?? item?.eur_try_rate);
  if (structuredCurrency === 'EUR' && Number.isFinite(structuredAmount) && Number.isFinite(structuredRate) && structuredRate > 0) {
    return { amount: Math.abs(structuredAmount), rate: structuredRate };
  }

  const description = String(item?.aciklama || item?.description || '');
  const match = description.match(/(?:^|\|)\s*(-?\d+(?:[.,]\d+)?)\s+EUR\s*;\s*EUR\/TRY=(-?\d+(?:[.,]\d+)?)/i);
  if (!match) return null;
  const amount = Number(match[1].replace(',', '.'));
  const rate = Number(match[2].replace(',', '.'));
  return Number.isFinite(amount) && Number.isFinite(rate) && rate > 0
    ? { amount: Math.abs(amount), rate }
    : null;
};

const formatNativeEuro = (amount) => `${Number(amount).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} €`;

const formatTransactionDate = (timestamp) => {
  const numeric = Number(timestamp);
  const date = Number.isFinite(numeric)
    ? new Date(numeric > 100000000000 ? numeric : numeric * 1000)
    : new Date(timestamp);

  if (Number.isNaN(date.getTime())) return 'Tarih bilgisi yok';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const getTransactionCategory = (description) => {
  const normalized = String(description || '').toLocaleLowerCase('tr-TR');

  if (normalized.includes('alt kullanıcı') || normalized.includes('kullanıcı ekleme')) {
    return { label: 'Alt Kullanıcı', Icon: PersonAddAltRoundedIcon };
  }
  if (normalized.includes('referans') || normalized.includes('bonus')) {
    return { label: 'Referans Bonusu', Icon: RedeemRoundedIcon };
  }
  if (normalized.includes('kredi yükleme') || normalized.includes('yükleme')) {
    return { label: 'Kredi Yükleme', Icon: AccountBalanceWalletRoundedIcon };
  }
  if (normalized.includes('abonelik') || normalized.includes('uzatma') || normalized.includes('süre')) {
    return { label: 'Abonelik', Icon: MoreTimeRoundedIcon };
  }
  if (normalized.includes('bayi') || normalized.includes('paket')) {
    return { label: 'Bayi Paketi', Icon: Inventory2RoundedIcon };
  }
  return { label: 'Genel İşlem', Icon: ReceiptLongRoundedIcon };
};

const getTransactionInfo = (item) => {
  const previous = numberValue(item.onceki);
  const next = numberValue(item.sonraki);
  const spent = numberValue(item.harcanan);
  const earned = numberValue(item.kazanc);
  const difference = next - previous;
  const failed = difference === 0 && (spent > 0 || earned > 0);
  const direction = failed
    ? (earned > 0 ? 'income' : 'expense')
    : difference > 0
      ? 'income'
      : difference < 0
        ? 'expense'
        : 'neutral';
  const attemptedAmount = direction === 'income' ? earned : spent;
  const amount = failed ? attemptedAmount : Math.abs(difference);
  const category = getTransactionCategory(item.aciklama);
  const fallbackMessage = failed
    ? (direction === 'income' ? 'Kredi yükleme denemesi' : 'Kredi harcama denemesi')
    : direction === 'income'
      ? 'Kredi yükleme işlemi'
      : direction === 'expense'
        ? 'Kredi harcama işlemi'
        : 'İşlem';

  return {
    ...category,
    previous,
    next,
    direction,
    failed,
    amount,
    message: String(item.aciklama || '').trim() || fallbackMessage,
    status: failed ? 'Başarısız' : direction === 'neutral' ? 'Tamamlandı' : 'Başarılı',
  };
};

function UserPanelTransactionHistoryModal({
  open,
  onClose,
  userInfo,
  previewMode = false,
  onFetchHistory,
  isReseller = false,
}) {
  const { currency, eurTryRate } = getCreditCurrency(userInfo, isReseller || previewMode);
  const formatCurrency = (amount) => formatCreditAmount(amount, currency, eurTryRate, { maximumFractionDigits: 0 });
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');

    const request = previewMode
      ? Promise.resolve({ history: PREVIEW_HISTORY })
      : typeof onFetchHistory === 'function'
        ? Promise.resolve().then(() => onFetchHistory({ userInfo, page: 1, limit: 1000 }))
        : Promise.reject(new Error('İşlem geçmişi bağlantısı sağlanmadı.'));

    request
      .then((result) => {
        if (cancelled) return;
        const records = Array.isArray(result) ? result : result?.history || result?.data?.history;
        const nextHistory = Array.isArray(records) ? [...records] : [];
        nextHistory.sort((first, second) => numberValue(second.id) - numberValue(first.id));
        setHistory(nextHistory);
      })
      .catch((requestError) => {
        if (!cancelled) {
          setHistory([]);
          setError(requestError?.message || 'İşlem geçmişi yüklenemedi.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [onFetchHistory, open, previewMode, userInfo]);

  useEffect(() => {
    if (open) return;
    setFilter('all');
    setPage(1);
  }, [open]);

  const transactions = useMemo(
    () => history.map((item) => ({ item, info: getTransactionInfo(item) })),
    [history],
  );
  const filteredTransactions = useMemo(
    () => transactions.filter(({ info }) => filter === 'all' || info.direction === filter),
    [filter, transactions],
  );
  const pages = Math.max(1, Math.ceil(filteredTransactions.length / PAGE_SIZE));
  const visibleTransactions = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredTransactions.slice(start, start + PAGE_SIZE);
  }, [filteredTransactions, page]);
  const activeFilterIndex = Math.max(0, FILTERS.findIndex((item) => item.id === filter));
  const filterTabIndicatorX = [
    '0px',
    'calc(100% + 5px)',
    'calc(200% + 10px)',
  ][activeFilterIndex];
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);

  const selectFilter = (nextFilter) => {
    setFilter(nextFilter);
    setPage(1);
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      keepMounted
      transitionDuration={0}
      disableEscapeKeyDown
      className="tvup-transaction-dialog"
      aria-labelledby="tvup-transaction-history-modal-title"
      slotProps={{
        backdrop: { className: 'tvup-transaction-backdrop' },
        paper: { id: 'tvup-transaction-history-modal', className: 'tvup-transaction-modal' },
      }}
    >
      <header className="tvup-transaction-header">
        <span className="tvup-transaction-title-icon" aria-hidden="true"><HistoryRoundedIcon /></span>
        <div>
          <h2 id="tvup-transaction-history-modal-title">İşlem Geçmişi</h2>
          <p>Kredi yükleme ve harcama kayıtlarınızı inceleyin.</p>
        </div>
        <button type="button" aria-label="İşlem geçmişini kapat" onClick={onClose}>
          <CloseRoundedIcon />
        </button>
      </header>

      <nav
        className="tvup-transaction-filters"
        role="tablist"
        aria-label="İşlem geçmişi türleri"
        style={{ '--tvup-transaction-tab-x': filterTabIndicatorX }}
      >
        {FILTERS.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            className={filter === id ? 'is-active' : ''}
            aria-selected={filter === id}
            onClick={() => selectFilter(id)}
          >
            <Icon />
            {label}
          </button>
        ))}
      </nav>

      <section className="tvup-transaction-content" aria-live="polite">
        {loading ? (
          <div className="tvup-transaction-state"><CircularProgress size={24} thickness={5} /><b>Kayıtlar yükleniyor</b></div>
        ) : error ? (
          <div className="tvup-transaction-state is-error"><ErrorOutlineRoundedIcon /><b>{error}</b></div>
        ) : visibleTransactions.length === 0 ? (
          <div className="tvup-transaction-state"><HistoryRoundedIcon /><b>{filter === 'all' ? 'Henüz işlem bulunmuyor.' : 'Bu filtrede kayıt bulunmuyor.'}</b></div>
        ) : (
          <div className="tvup-transaction-log-list">
            {visibleTransactions.map(({ item, info }, index) => {
              const TransactionIcon = info.Icon;
              const amountSign = info.direction === 'expense' ? '-' : info.direction === 'income' ? '+' : '';
              const toneClass = info.failed ? 'is-failed' : `is-${info.direction}`;
              const originalQuote = currency === 'EUR' ? originalEurQuote(item) : null;
              const formatTransactionCurrency = (amount, preferNative = false) => {
                if (originalQuote) {
                  return preferNative
                    ? formatNativeEuro(originalQuote.amount)
                    : formatCreditAmount(amount, 'EUR', originalQuote.rate, { maximumFractionDigits: 0 });
                }
                return formatCurrency(amount);
              };
              const formattedAmount = formatTransactionCurrency(info.amount, true);

              return (
                <article key={item.id || `${filter}-${index}`} className={toneClass}>
                  <span className="tvup-transaction-log-icon" aria-hidden="true"><TransactionIcon /></span>
                  <div className="tvup-transaction-log-copy">
                    <span className="tvup-transaction-log-heading">
                      <b>{info.label}</b>
                      <small>{info.status}</small>
                    </span>
                    <span>{currency !== 'TRY' && /\b(?:TL|TRY)\b|₺/i.test(info.message) ? info.label : info.message}</span>
                  </div>
                  <div className="tvup-transaction-log-meta">
                    <b>{formattedAmount === '—' ? '—' : `${amountSign}${formattedAmount}`}</b>
                    <span>{formatTransactionDate(item.zaman || item.timestamp)}</span>
                    <small>{formatTransactionCurrency(info.previous)} → {formatTransactionCurrency(info.next)}</small>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <footer className="tvup-transaction-footer">
        <button type="button" aria-label="Önceki sayfa" disabled={loading || page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>
          <NavigateBeforeRoundedIcon /> Önceki
        </button>
        <span>{page} / {pages}</span>
        <button type="button" aria-label="Sonraki sayfa" disabled={loading || page >= pages} onClick={() => setPage((current) => Math.min(pages, current + 1))}>
          Sonraki <NavigateNextRoundedIcon />
        </button>
      </footer>
    </Dialog>
  );
}

export default UserPanelTransactionHistoryModal;
